import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ReactElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import TooltipDialog from '../../components/dialog/tooltip/TooltipDialog';
import IconProvider from '../../components/icons/IconProvider';
import { ISimulatorContext, SimulatorContext } from '../../context/SimulatorProvider';
import { Product } from '../../data/product';
import { PriceComponent, Restrictions } from '../../data/tariff';
import './SimulationRecap.css';
import useGameLink from '../../hooks/useGameLink';
/* VisualRef StandBy
import { Service } from '../../data/service';
import List from '@mui/material/List';
import { ListItem } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
*/

function SimulationRecap() {
    const { t } = useTranslation()
    const { codeTotal, tariff, selectedProduct, getEstimatedPrice } = useContext(SimulatorContext) as ISimulatorContext
    const { GetGameLink } = useGameLink()
    const startTime = new Date()
    const endTime = new Date(startTime)
    const simulationTime = tariff?.find(t => t.serviceId === selectedProduct?.services[0].id)?.durationInMinutes
    const batteryLevel = tariff?.find(t => t.serviceId === selectedProduct?.services[0].id)?.batteryTargetLevel
    const energyQuantity = tariff?.find(t => t.serviceId === selectedProduct?.services[0].id)?.energyQuantity
    const dialogContent = [
        [t("station-assets-resume.dialog-title"), t("station-assets-resume.dialog-content-text")]
    ]

    simulationTime && endTime.setMinutes(startTime.getMinutes() + simulationTime)

    const getTariffFormula = (tariffId?: string) => {
        const tariffElements = tariff?.find(t => t.tariffModel.id === tariffId)?.tariffModel.elements;
        let energyPrice = 0;
        return tariffElements!.map((tariffElements, key) => {
            let details: ReactElement[] = [];
            const restrictions = tariffElements.Restrictions;
            const priceComponents = tariffElements.PriceComponents;

            priceComponents.forEach((pc, pcKey) => {
                if (pc.Type === 0) {
                    energyPrice = (pc.Price * 1.20);
                    details.push(<span key={"formula-" + pcKey}>{t("station-assets-resume.tariffEnergy", { price: energyPrice.toFixed(2) })}<br /></span>);
                } else if (pc.Type === 3) {
                    const timePrice = (pc.Price * 1.20) / 60;
                    if (timePrice !== 0) {
                        details.push(addTimePriceDetails(pc, pcKey, restrictions, energyPrice, timePrice)!)
                    }
                }
            })

            return details ?? (<span key={"formula-" + key}>{details}</span>)
        });
    }

    const addTimePriceDetails = (pc: PriceComponent, pcKey: number, restrictions: Restrictions, energyPrice: number, timePrice: number) => {
        if (restrictions === null || (!restrictions.MinDuration && !restrictions.MaxDuration)) {
            return <span key={"formula-" + pcKey}>{`${timePrice.toFixed(2)}€ / min`}<br /></span>;
        } else if ((!restrictions.MinDuration || restrictions.MinDuration <= 0) && restrictions.MaxDuration) {
            return <span key={"formula-" + pcKey}>{t("station-assets-resume.tariffMin", { maxDuration: (Math.round((restrictions.MaxDuration / 60))), price: timePrice.toFixed(2) })}<br /></span>;
        } else if (restrictions.MinDuration && restrictions.MaxDuration) {
            const minDuration = restrictions.MinDuration / 60;
            return (minDuration < 1) ?
                <span key={"formula-" + pcKey}>{t("station-assets-resume.tariffMin", { maxDuration: (Math.round((restrictions.MaxDuration / 60))), price: timePrice.toFixed(2) })}<br /></span>
                : <span key={"formula-" + pcKey}>{t("station-assets-resume.tariffInterval", { minDuration: (Math.round((restrictions.MinDuration / 60))), maxDuration: (Math.round(restrictions.MaxDuration / 60)), price: timePrice.toFixed(2) })}<br /></span>;
        } else if (restrictions.MinDuration && !restrictions.MaxDuration) {
            return (energyPrice !== 0) ?
                <span key={"formula-" + pcKey}>{t("station-assets-resume.tariffMax", { minDuration: (Math.round((restrictions.MinDuration / 60))), price: timePrice.toFixed(2) }) + " + " + energyPrice.toFixed(2) + "€ / kWh"}<br /></span>
                : <span key={"formula-" + pcKey}>{t("station-assets-resume.tariffMax", { minDuration: (Math.round((restrictions.MinDuration / 60))), price: timePrice.toFixed(2) })}<br /></span>;
        }
    }

    const minToHours = (nbMinutes: number) => {
        const padTo2Digits = (num: number) => (num.toString().padStart(2, '0'))
        const minutes = nbMinutes % 60;
        const hours = Math.floor(nbMinutes / 60);

        return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;
    }

    const renderConnectorIllustration = (power: number) => {
        switch (power) {
            case 300:
                return (
                    <div className="img-container">
                        <img src={process.env.PUBLIC_URL + "/assets/EVSE300.svg"} alt="EVSE" className="illustration-size" />
                    </div>)
            case 175:
                return (
                    <div className="img-container">
                        <img src={process.env.PUBLIC_URL + "/assets/EVSE175.png"} alt="EVSE" className="illustration-size" />
                    </div>)
            case 50:
                return (
                    <div className="img-container">
                        <img src={process.env.PUBLIC_URL + "/assets/EVSE43_50.png"} alt="EVSE" className="illustration-size" />
                    </div>)
            case 43:
                return (
                    <>
                        <div className="img-container">
                            <img src={process.env.PUBLIC_URL + "/assets/EVSE43.png"} alt="EVSE" className="illustration-size" />
                        </div>
                        <div className="img-container">
                            <img src={process.env.PUBLIC_URL + "/assets/EVSE43_50.png"} alt="EVSE" className="illustration-size" />
                        </div>
                    </>
                )
            default:
                break
        }
    }

    const renderGlobalStatus = (product: Product) => {
        let statusClass = ""

        if (product.availableAssetCount > 0) {
            statusClass = "status-Available"
        }
        else if (product.occupiedAssetCount > 0) {
            statusClass = "status-InUse"
        }
        else if (product.unknownAssetCount > 0) {
            statusClass = "status-Unknown"
        }

        let statusMessage = t(statusClass?.replace("-", "."))

        return (
            (statusClass !== "") && <Typography className={`status ${statusClass}`} variant="caption" component="span" color="primary">
                {statusMessage}
            </Typography>
        )
    }

    /* Add visualRef StandBy
    const renderServices = (product: Product) => {
        let servicesStatusFiltered: Service[] = [];

        if (product.availableAssetCount > 0) {
            console.log(product.services)
            servicesStatusFiltered = product.services.filter(service => service.status === "Available")
        }
        else if (product.occupiedAssetCount > 0) {
            servicesStatusFiltered = product.services.filter(service => service.status === "InUse")
        }
        else if (product.unknownAssetCount > 0) {
            servicesStatusFiltered = product.services.filter(service => service.status === "Unknown")
        }
        return (
            servicesStatusFiltered.length > 0 &&
            <List disablePadding>
                {servicesStatusFiltered.map((item: Service, key: any) => (
                    <ListItem key={key} className='render-status' divider={key + 1 === servicesStatusFiltered.length ? false : true} disablePadding>
                        <Typography variant="subtitle1">
                            {t("station-assets-resume.service-visual-ref")}{item.visualReference !== null ? item.visualReference : item.physicalReference}
                        </Typography>
                    </ListItem>
                )
                )}
            </List>

        )
    }

  
    {(selectedProduct?.assetCount) &&
                <section className='simulation-recap-card recap-time-card'>
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            className="render-status"
                        >
                            {renderGlobalStatus(selectedProduct)}
                        </AccordionSummary>
                        <AccordionDetails>
                            {renderServices(selectedProduct)}
                        </AccordionDetails>
                    </Accordion>
                </section>
            }
    */
    return (
        <Stack textAlign={"center"}>
            <Typography variant="body1">{t("station-assets-resume.body1")}</Typography>
            {(selectedProduct?.assetCount) &&
                <section className='simulation-recap-card recap-card'>
                    <div className='recap-card-container'>
                        <div className='recap-card-infos'>
                            <div className='recap-card-product'>
                                <Typography variant="h5" marginBottom={1} color="secondary">
                                    {t("station-assets-resume.selected-product-power-type", { powerType: t("power-type." + selectedProduct.services[0].connectors[0]?.powerType) })}<br />
                                    {t("station-assets-resume.selected-product-power", { power: selectedProduct.services[0].connectors[0]?.displayPower })}
                                </Typography>
                                {renderGlobalStatus(selectedProduct)}
                            </div>
                            <div className='recap-card-info-connector'>
                                <IconProvider id={selectedProduct.services[0].connectors[0]?.standard} size="large" />
                                <span className="connector-standard">
                                    {t("connector." + selectedProduct.services[0].connectors[0]?.standard)}
                                </span>
                            </div>
                        </div>
                        <div className='recap-card-connector'>
                            <div className='illustrations-card'>
                                {renderConnectorIllustration(selectedProduct.services[0].connectors[0]?.displayPower)}
                            </div>
                        </div>
                    </div>
                </section>
            }
            {GetGameLink()}
            <section className='simulation-recap-card recap-time-card'>
                <Typography variant="h5" textAlign={"center"}>{t("station-assets-resume.estimation-title")}</Typography>
                {simulationTime &&
                    <div className='estimation-details-container'>
                        <div>
                            <IconProvider id="chrono" color="red-filter" size="small" />
                            <Typography variant="subtitle1">
                                {t("station-assets-resume.estimated-time")}
                            </Typography>
                            <Chip label={(minToHours(simulationTime))} />
                        </div>
                        <div>
                            <IconProvider id="time" color="red-filter" size="small" />
                            <Typography variant="subtitle1">
                                {t("station-assets-resume.end-time")}
                            </Typography>
                            <Chip label={endTime.toLocaleTimeString('fr-Fr', { hour: '2-digit', minute: '2-digit' })} />
                        </div>
                    </div>}
                <Divider variant="middle" />
                {batteryLevel &&
                    <div className='estimation-details-container'>
                        <div>
                            <IconProvider id="battery" color="red-filter" size="small" />
                            <Typography variant="subtitle1">
                                {t("station-assets-resume.estimated-battery")}
                            </Typography>
                            <Chip label={batteryLevel + "%"} />
                        </div>
                        <div>
                            <IconProvider id="lightning" color="red-filter" size="small" />
                            <Typography variant="subtitle1">
                                {t("station-assets-resume.estimated-quantity")}
                            </Typography>
                            <Chip label={energyQuantity + " kWh"} />
                        </div>
                    </div>}
                <Divider variant="middle" />
                {(selectedProduct?.assetCount) && <div className='recap-tariff-card'>
                    <IconProvider id="price" color="red-filter" size="small" />
                    <div>
                        <Typography variant="subtitle1">
                            {t("station-assets-resume.estimated-price")}
                        </Typography>
                    </div>
                    <Chip label={(getEstimatedPrice && getEstimatedPrice(selectedProduct.services[0].id) + "€") ?? "-"} />
                    <div>
                        <Typography variant="subtitle1">
                            {t("station-assets-resume.tariff")}
                        </Typography>
                        <section className="tariff-formula-chip">{getTariffFormula(selectedProduct.services[0].connectors[0]?.tariffId) ?? "-"}</section>
                    </div>
                    <div>
                        <span className="resume-disclaimer" >
                            {t("station-assets-resume.disclaimer")}
                            <TooltipDialog button={t("station-assets-resume.dialog-button")} content={dialogContent} steps={1} action={t("station-assets-resume.dialog-action")} />
                        </span>
                    </div>
                </div>}
            </section>
            <div className="asset-count">
                <IconProvider id="info" size="small" color="blue-filter" />
                <Typography variant="caption" color="#285AFF">
                    {t("station-assets-resume.assetCount", { assetCount: selectedProduct?.assetCount, availableAssetCount: selectedProduct?.availableAssetCount })}
                </Typography>
            </div>
            <div className='payment-container'>
                <Button className="drawer-button payment-button" variant="outlined" color="secondary" target="_blank" href={`https://apps.total-ev-charge.com/charge-points/TOTAL-${codeTotal}`}>
                    <IconProvider id="payment" color="red-filter" size="small" /><span>{t("link-payment.confirm-action")}</span>
                </Button>
            </div>
        </Stack>
    );
}

export default SimulationRecap;